import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { findDoc, getDoc } from "../../lib/docs/loader";
import { docPathForPath } from "../../lib/docs/module-map";
import { setDocsPaneOpen } from "../../lib/docs/pane-state";
import MarkdownRenderer from "./MarkdownRenderer";
import SourceCard from "./SourceCard";

// In-flow right-column pane that shows the current route's module spec.
// Rendered as a sibling of <main> in App.tsx, not as a fixed overlay —
// module content stays fully interactive on the left.
//
// Follows the current route via useLocation. If the route has no mapped
// module doc, the pane shows a placeholder rather than closing.

export default function ModuleDocPane() {
  const [pathname] = useLocation();
  const routeDocPath = useMemo(() => docPathForPath(pathname), [pathname]);

  const [docPath, setDocPath] = useState<string | null>(routeDocPath);
  const [, setCheckboxTick] = useState(0);

  useEffect(() => setDocPath(routeDocPath), [routeDocPath]);

  const doc = useMemo(() => (docPath ? getDoc(docPath) : null), [docPath]);

  function handleNavigate(target: string) {
    const resolved = findDoc(target);
    if (resolved) setDocPath(resolved.path);
    else console.warn(`Doc not found: ${target}`);
  }

  return (
    <aside className="w-96 flex-none h-full border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131825] flex flex-col">
      <header className="flex-none flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-mono truncate">
            {docPath ?? "(no doc mapped)"}
          </p>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
            {doc?.title ?? (docPath ? "Document not found" : "Docs")}
          </p>
        </div>
        <button
          onClick={() => setDocsPaneOpen(false)}
          aria-label="Close docs pane"
          className="ml-3 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 cursor-pointer"
          title="Close docs pane"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <SourceCard pathname={pathname} />
        {doc ? (
          <MarkdownRenderer
            content={doc.content}
            docPath={doc.path}
            onNavigate={handleNavigate}
            onCheckboxChange={() => setCheckboxTick((t) => t + 1)}
          />
        ) : (
          <p className="text-sm text-slate-500">
            No documentation is mapped for <code className="font-mono text-xs">{pathname}</code>.
          </p>
        )}
      </div>
    </aside>
  );
}
